'use client';

import { blackTransparentLoaderModalAtom, centerToastModalAtom } from '@/atom/modal';
import { ResponseStatusCGType } from '@/types/graphql/root';
import { reportTravelReview, reportTravelReviewComment } from '@/utils/actions';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';

const reportReasons = [
  {
    id: 1,
    label: '스팸홍보/도배글입니다',
    description: '동일한 내용을 반복 게시하거나 광고성 콘텐츠를 게시한 경우',
  },
  {
    id: 2,
    label: '음란물입니다',
    description: '성적인 콘텐츠 또는 음란한 내용을 포함한 경우',
  },
  {
    id: 3,
    label: '불법정보를 포함하고 있습니다',
    description: '불법적인 거래, 사기 등 법률에 위반되는 내용',
  },
  {
    id: 4,
    label: '욕설/혐오/차별적 표현입니다',
    description: '타인을 비하하거나 차별하는 내용을 포함한 경우',
  },
  {
    id: 5,
    label: '개인정보 노출 게시물입니다',
    description: '타인의 개인정보를 동의 없이 노출한 경우',
  },
  {
    id: 6,
    label: '저작권을 침해하는 내용입니다',
    description: '타인의 저작물을 무단으로 사용한 경우',
  },
  {
    id: 7,
    label: '명예훼손/허위사실 유포입니다',
    description: '타인의 명예를 훼손하거나 사실이 아닌 내용을 유포하는 경우',
  },
];

export default function SharingTripReportModal({
  visible,
  setVisible,
  reviewId,
  commentId,
}: {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  reviewId: number;
  commentId: number | null;
}) {
  const setBlackTransparentLoaderModal = useSetAtom(blackTransparentLoaderModalAtom);
  const setCenterToastModal = useSetAtom(centerToastModalAtom);

  const [selectedReason, setSelectedReason] = useState<number | null>(null);

  useEffect(() => {
    setSelectedReason(null);
  }, [visible]);

  const onClickReport = useCallback(async () => {
    if (!selectedReason) {
      setCenterToastModal({
        text: '신고 사유를 선택해주세요',
        renderingTime: 1500,
      });
      return;
    }

    try {
      setBlackTransparentLoaderModal(true);
      if (commentId) {
        const result = await reportTravelReviewComment(commentId, reviewId, reportReasons[selectedReason - 1].label);
        if (result && result === ResponseStatusCGType.OkCGType) {
          setCenterToastModal({
            text: '신고가 완료되었습니다.',
            renderingTime: 1500,
          });
          setVisible(false);
        } else {
          throw new Error('신고에 실패하였습니다.');
        }
      } else {
        const result = await reportTravelReview(reviewId, reportReasons[selectedReason - 1].label);
        if (result && result === ResponseStatusCGType.OkCGType) {
          setCenterToastModal({
            text: '신고가 완료되었습니다.',
            renderingTime: 1500,
          });
          setVisible(false);
        } else {
          throw new Error('신고에 실패하였습니다.');
        }
      }
    } catch (error) {
      console.error(error);
      setCenterToastModal({
        text: '신고에 실패하였습니다.',
        renderingTime: 1500,
      });
    } finally {
      setBlackTransparentLoaderModal(false);
    }
  }, [selectedReason]);

  if (!visible) return null;
  return (
    <div
      className='fixed w-full max-w-480pxr top-0 z-20 h-dvh overflow-hidden bg-custom-black/60 flex items-center justify-center'
      onClick={(e) => {
        setVisible(false);
      }}
    >
      <div className='w-[90%] bg-white rounded-12pxr shadow-clear p-20pxr' onClick={(e) => e.stopPropagation()}>
        <div className='flex flex-col gap-8pxr max-h-[50dvh] overflow-y-scroll'>
          {reportReasons.map((reason) => (
            <button
              key={'reason_' + reason.label}
              className={`p-8pxr flex flex-col bg-white rounded-6pxr gap-4pxr items-start ${
                selectedReason === reason.id ? 'border-2 border-custom-black' : 'border-2 border-gray-200'
              }`}
              onClick={() => {
                setSelectedReason(reason.id);
              }}
            >
              <h2 className='text-gray-900 body-16-md text-left whitespace-pre-line'>{reason.label}</h2>
              <p className='text-gray-700 body-12-regular text-left whitespace-pre-line'>{reason.description}</p>
            </button>
          ))}
        </div>

        <div className='mt-20pxr w-full flex flex-row justify-end gap-8pxr'>
          <button
            className='flex-1 rounded-6pxr border border-gray-300 text-gray-700 py-10pxr'
            onClick={() => {
              setVisible(false);
            }}
          >
            취소
          </button>
          <button
            className='flex-1 rounded-6pxr bg-custom-black text-white'
            onClick={() => {
              onClickReport();
            }}
          >
            신고하기
          </button>
        </div>
      </div>
    </div>
  );
}
